/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useRef } from 'react'
import styled from 'styled-components'
import { Field, Form, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm, Lightbox, Theme } from '@nexigendigital/nxui'
import { addEPPCode } from 'store/slices/shopSlice'
import { Weight } from 'models/enums'
import { useAppDispatch } from 'store/hooks'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    domain: {
        product: {
            id: number
        },
        meta: {
            require_epp_code: boolean
        }
    },
    name: string,
    status: boolean,
    close: () => void
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function EPPLightbox({ domain, name, status, close }: Props) {
    const dispatch = useAppDispatch()
    const formRef = useRef<FormikProps<{ epp: string }>>(null)
    const { meta: { require_epp_code } } = domain

    function handleSubmit() {
        if (formRef.current) {
            formRef.current.handleSubmit()
        }
    }

    const form = () => (
        <Formik
            innerRef={formRef}
            initialValues={{
                epp: ''
            }}
            validationSchema={
                Yup.object({
                    epp: Yup.string()
                })
            }
            onSubmit={({ epp }) => {
                dispatch(addEPPCode({ name, epp, product_id: domain.product.id }))
                close()
            }}
        >
            {({ isSubmitting }) => (
                <Form>
                    <Description>
                        A special transfer code obtained through your current provider is required to transfer this
                        domain name.
                    </Description>
                    <Field
                        label={'EPP Code'}
                        name={'epp'}
                        type={'text'}
                        disabled={isSubmitting}
                        component={NXUIForm.InputField}
                    />
                    <Note>
                        Be sure to copy and paste the EPP Code to avoid adding or missing any characters.
                        If you don&apos;t currently have the EPP Code,
                        simply proceed with the transfer by clicking SAVE below and providing the correct EPP Code
                        after-the-fact.
                    </Note>
                </Form>
            )}
        </Formik>
    )

    if (require_epp_code) {
        return (
            <Lightbox
                title={'Transfer Code'}
                content={form()}
                conditions={{ state: status, action: () => close() }}
                links={[]}
                actions={[
                    {
                        label: 'Back',
                        color: 'flat',
                        type: 'button',
                        func: () => close()
                    },
                    {
                        label: 'Save',
                        color: 'primary',
                        type: 'button',
                        func: () => handleSubmit()
                    }
                ]}
            />
        )
    }

    return null
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const Description = styled.h2`
    max-width: 467px;
    font-size: .875rem;
    color: ${props => props.theme['text--100']};
    margin-bottom: 20px;
`

const Note = styled.aside`
    max-width: 467px;
    padding: 12px;
    font-size: 12px;
    ${Theme.Functions.setFont('Inter', Weight.Medium)};
    background-color: #ffeeb8;
    border: 1px solid #ffc438;
    border-radius: 8px;
`
